import { useCallback, useMemo, useRef, useState, useTransition } from 'react';
import type { Selection } from 'react-aria-components';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableViewCreationFromSelect, enableViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackButton } from '@gonfalon/feedback/ContextualFeedbackButton';
import type { UIFlagFilter } from '@gonfalon/flag-filters/types';
import { SearchField } from '@gonfalon/launchpad-experimental/Form';
import type { SectionedData } from '@gonfalon/launchpad-experimental/ResourceSelector';
import { ResourceSelector, SearchResults } from '@gonfalon/launchpad-experimental/ResourceSelector';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { AccessCheck } from '@gonfalon/permissions/access-check';
import { getProjectQuery } from '@gonfalon/rest-api/queries/projects';
import { getViewsQuery } from '@gonfalon/rest-api/queries/views';
import { Box } from '@launchpad-ui/box';
import {
  Button,
  ButtonGroup,
  IconButton,
  ListBoxItem,
  ProgressBar,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import type { ViewSelectedResource } from '../components/AddResourcesToViewModal';
import { VIEWS_QUERY_STALE_TIME } from '../constants';
import type { LinkableResourceType, View } from '../types';
import { useViewCreation } from '../ViewCreationContext';

import { ProjectEmptyViewsState } from './ProjectEmptyViewsState';
import { useFavoriteViews } from './useFavoriteViews';
import { ViewDetailsPanel } from './ViewDetailsPanel';

import styles from './RichViewsDialog.module.css';

const RICH_SELECTOR_EXPANDED_CONTAINER_STYLE = {
  flex: '1 1 auto',
  flexGrow: 1,
  minHeight: 0,
  height: 'auto',
  width: '100%',
  gridTemplateColumns: '1fr 1fr',
} as const;

const RICH_SELECTOR_COLLAPSED_CONTAINER_STYLE = {
  flex: '1 1 auto',
  flexGrow: 1,
  minHeight: 0,
  height: 'auto',
  width: 'var(--lp-size-320)',
} as const;

const RICH_SELECTOR_HEADER_STYLE = {
  padding: 'var(--lp-spacing-300)',
  borderBottom: '1px solid var(--lp-color-border-ui-primary)',
} as const;

export type RichViewsDialogProps = {
  onSelectionChange: (views: View[]) => void;
  selectedViews: View[];
  label?: string;
  selectionMode?: 'single' | 'multiple';
  allowClear?: boolean;
  onBeforeCreateView?: () => void;
  onFeedbackPress?: () => void;
  onToggleDetails?: () => void;
  isExpanded?: boolean;
  hideSelectionFooter?: boolean;
  flagFilters?: UIFlagFilter;
  resourceType?: LinkableResourceType;
  initialSelectedResources?: Array<ViewSelectedResource<LinkableResourceType>>;
  /**
   * When true, suppress the header `Create` affordance. Useful in filter
   * contexts where inline view creation doesn't make sense (e.g. the SDK Keys
   * view filter — see payload-filtering-v2 spec §15.4).
   */
  hideCreateButton?: boolean;
  /**
   * Project key to scope the views query against. When omitted, falls back to the active
   * `useProjectContext()` — required for callers mounted under the `project-context` route loader.
   *
   * Callers mounted **outside** the project-context loader (e.g. payload-filtering-v2's
   * `NewSdkKeyModal` at `/settings/sdk-keys` — spec §1.1) MUST pass this explicitly.
   */
  projectKey?: string;
};

export function RichViewsDialog({ projectKey: projectKeyProp, ...props }: RichViewsDialogProps) {
  // Fall back to the active project context when the caller doesn't supply a projectKey. The
  // `optional: true` flag prevents the invariant from firing when this dialog is mounted under a
  // route without the project-context loader (e.g. `/settings/sdk-keys`); in that case the caller
  // MUST pass `projectKey` explicitly — we throw if neither source resolves.
  const projectContext = useProjectContext({ optional: true });
  const projectKey = projectKeyProp ?? projectContext?.project.key;
  // Resolve `projectKey` and guard here — before any other hooks run — so the conditional throw can
  // never change the hook count across renders. The hook-heavy body lives in `RichViewsDialogContent`,
  // which only mounts once we have a guaranteed `projectKey`.
  if (!projectKey) {
    throw new Error(
      'RichViewsDialog requires a `projectKey` prop when rendered outside of the project-context route loader.',
    );
  }
  // Only forward the context project when the caller didn't override `projectKey`. Otherwise the
  // content component would prefer the context's project over the one fetched for the explicit
  // `projectKeyProp`, handing a mismatched project to downstream `AccessCheck` wrappers.
  return (
    <RichViewsDialogContent
      {...props}
      projectKey={projectKey}
      contextProject={projectKeyProp ? undefined : projectContext?.project}
    />
  );
}

type RichViewsDialogContentProps = Omit<RichViewsDialogProps, 'projectKey'> & {
  projectKey: string;
  contextProject?: NonNullable<ReturnType<typeof useProjectContext>>['project'];
};

function RichViewsDialogContent({
  onSelectionChange,
  selectedViews = [],
  label = 'Select views',
  selectionMode = 'single',
  allowClear,
  onBeforeCreateView,
  onFeedbackPress,
  onToggleDetails,
  isExpanded = true,
  hideSelectionFooter = false,
  flagFilters,
  resourceType,
  initialSelectedResources,
  hideCreateButton = false,
  projectKey,
  contextProject,
}: RichViewsDialogContentProps) {
  const [filterText, setFilterText] = useState('');
  const [isTransitioning, startTransition] = useTransition();
  // `project` is required by downstream `AccessCheck` wrappers. Inside the project-context loader
  // we already have it from `contextProject`; outside (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`)
  // we suspense-fetch from the resolved `projectKey`. The query is cached by React Query, so when
  // the project context already populated it (in-loader path) this is effectively free.
  const { data: fetchedProject } = useSuspenseQuery(getProjectQuery({ projectKey }));
  const project = contextProject ?? fetchedProject;

  const { openCreateModal, setOnViewCreated } = useViewCreation();
  const isCreateViewFeatureEnabled = enableViews() && enableViewCreationFromSelect() && !hideCreateButton;
  const canRenderFeedback = Boolean(onFeedbackPress);

  const selectedViewsRef = useRef(selectedViews);
  const onSelectionChangeRef = useRef(onSelectionChange);
  selectedViewsRef.current = selectedViews;
  onSelectionChangeRef.current = onSelectionChange;

  const handleCreateClick = useCallback(() => {
    onBeforeCreateView?.();
    setOnViewCreated((view) => {
      onSelectionChangeRef.current([...selectedViewsRef.current, view]);
    });
    openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources);
  }, [
    filterText,
    flagFilters,
    resourceType,
    initialSelectedResources,
    onBeforeCreateView,
    openCreateModal,
    setOnViewCreated,
  ]);

  const { data: viewsRes, isPending } = useSuspenseQuery({
    ...getViewsQuery({
      projectKey,
      query: filterText ? { filter: { query: filterText } } : undefined,
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEWS_QUERY_STALE_TIME,
  });

  const allViews = useMemo(
    () => uniqBy([...selectedViews, ...(viewsRes?.items ?? [])], 'key'),
    [viewsRes, selectedViews],
  );

  const isFiltered = filterText.trim().length > 0;
  const {
    canToggleFavorites,
    shouldRenderSections,
    favoriteViewKeys,
    orderedFavoriteViews,
    orderedAllNonFavoriteViews,
    handleFavoriteToggle,
  } = useFavoriteViews({ projectKey, allViews, selectedViews, selectionMode, isFiltered });

  const selectedKeys = useMemo(() => new Set(selectedViews.map((view) => view.key)), [selectedViews]);
  const isLoading = isTransitioning || isPending;
  const toggleDetailsLabel = isExpanded ? 'Collapse details' : 'Expand details';
  const toggleDetailsIcon = isExpanded ? 'sidebar-right-collapse' : 'sidebar-right-expand';
  const containerStyle = isExpanded ? RICH_SELECTOR_EXPANDED_CONTAINER_STYLE : RICH_SELECTOR_COLLAPSED_CONTAINER_STYLE;

  const handleFilterChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (typeof selection === 'string') {
        const selectedView = allViews.find((view) => view.key === selection);
        onSelectionChange(selectedView ? [selectedView] : []);
        return;
      }

      const keys = Array.from(selection);
      if (keys.length > 0) {
        const views = keys.map((key) => allViews.find((v) => v.key === key)).filter((v) => !!v);
        onSelectionChange(views);
        return;
      }

      onSelectionChange([]);
    },
    [allViews, onSelectionChange],
  );

  const viewsData: SectionedData<View> | View[] = shouldRenderSections
    ? [
        { name: 'Favorites', items: orderedFavoriteViews },
        ...(orderedAllNonFavoriteViews.length > 0 ? [{ name: '', items: orderedAllNonFavoriteViews }] : []),
      ]
    : allViews;

  const renderDetails = useCallback(
    (view: View | null) => {
      if (!view) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="full">
            <ProgressBar size="small" isIndeterminate />
          </Box>
        );
      }

      return (
        <ViewDetailsPanel
          key={view.key}
          view={view}
          isFavorite={favoriteViewKeys.has(view.key)}
          onToggleFavorite={() => handleFavoriteToggle(view.key)}
          canToggleFavorites={canToggleFavorites}
          projectKey={projectKey}
        />
      );
    },
    [favoriteViewKeys, handleFavoriteToggle, canToggleFavorites, projectKey],
  );

  const isProjectEmpty = !isLoading && filterText.trim() === '' && allViews.length === 0;
  // Suppress the header Create CTA when `ProjectEmptyViewsState` renders — the
  // empty state already owns the primary CTA, so avoid a duplicate.
  const shouldRenderHeaderCreate = isCreateViewFeatureEnabled && !isProjectEmpty;

  const renderEmptyState = useCallback(() => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" padding="$600">
          <ProgressBar aria-label="Loading..." isIndeterminate size="small" />
        </Box>
      );
    }
    if (filterText.trim() !== '') {
      // Search no-match — keep existing behavior.
      return (
        <Box display="flex" justifyContent="center" alignItems="center" padding="$600">
          <>No views found</>
        </Box>
      );
    }
    // Project has zero views — centered block with Create CTA (see §6.1).
    // Mirror the header gating: suppress the empty-state Create button whenever
    // the create-view feature itself is disabled (feature flags off, or caller
    // passed `hideCreateButton`). Otherwise we'd render a Create CTA here that
    // the rest of the dialog intentionally hides.
    return <ProjectEmptyViewsState onCreateView={handleCreateClick} hideCreateButton={!isCreateViewFeatureEnabled} />;
  }, [isLoading, filterText, handleCreateClick, isCreateViewFeatureEnabled]);

  const hasFooter = allowClear || canRenderFeedback;

  return (
    <div className={styles.richViewsContainer}>
      <ResourceSelector<View>
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        selectionMode={selectionMode}
        renderDetails={isExpanded ? renderDetails : undefined}
        renderEmptyState={renderEmptyState}
        containerStyle={containerStyle}
        headerStyle={RICH_SELECTOR_HEADER_STYLE}
        hideFooter={hideSelectionFooter}
        aria-label={label}
      >
        <div slot="header">
          <SearchField
            autoFocus
            aria-label="Search views"
            isBusy={isTransitioning}
            onChange={handleFilterChange}
            placeholder={isExpanded ? 'Search views...' : 'Search...'}
          />
          {(shouldRenderHeaderCreate || onToggleDetails) && (
            <>
              {isExpanded ? (
                <div className={styles.headerActionGroupExpanded}>
                  {shouldRenderHeaderCreate && (
                    <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                      <Button variant="default" size="medium" onPress={handleCreateClick}>
                        <Icon name="add" size="small" /> Create
                      </Button>
                    </AccessCheck>
                  )}
                  {onToggleDetails && (
                    <TooltipTrigger>
                      <IconButton
                        aria-label={toggleDetailsLabel}
                        icon={toggleDetailsIcon}
                        size="medium"
                        variant="default"
                        className={styles.detailsToggleButton}
                        onPress={onToggleDetails}
                      />
                      <Tooltip>{toggleDetailsLabel}</Tooltip>
                    </TooltipTrigger>
                  )}
                </div>
              ) : (
                <ButtonGroup spacing="compact" className={styles.headerActionGroupCollapsed}>
                  {shouldRenderHeaderCreate && (
                    <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                      <TooltipTrigger>
                        <IconButton
                          aria-label="Create view"
                          icon="add"
                          size="medium"
                          variant="default"
                          onPress={handleCreateClick}
                        />
                        <Tooltip>Create view</Tooltip>
                      </TooltipTrigger>
                    </AccessCheck>
                  )}
                  {onToggleDetails && (
                    <TooltipTrigger>
                      <IconButton
                        aria-label={toggleDetailsLabel}
                        icon={toggleDetailsIcon}
                        size="medium"
                        variant="default"
                        className={styles.detailsToggleButton}
                        onPress={onToggleDetails}
                      />
                      <Tooltip>{toggleDetailsLabel}</Tooltip>
                    </TooltipTrigger>
                  )}
                </ButtonGroup>
              )}
            </>
          )}
        </div>
        <SearchResults<View>
          items={viewsData}
          getItemKey={(view) => view.key}
          isLoading={isLoading && allViews.length === 0}
          resultsAreaClassName={styles.richResultsArea}
          aria-label="Views"
        >
          {(view) => (
            <ListBoxItem key={view.key} id={view.key} textValue={view.name}>
              <div className={styles.viewListItem}>
                <Icon name="stack" size="small" className={styles.viewNameIcon} />
                <TextTruncator>{view.name}</TextTruncator>
              </div>
            </ListBoxItem>
          )}
        </SearchResults>
      </ResourceSelector>

      {hasFooter && (
        <div className={styles.richFooter}>
          <div className={styles.richFooterLeft}>
            {canRenderFeedback && <ContextualFeedbackButton size="small" variant="minimal" onPress={onFeedbackPress} />}
          </div>
          <div className={styles.richFooterRight}>
            {allowClear && selectedViews.length > 0 && (
              <Button size="small" variant="default" onPress={() => onSelectionChange([])}>
                <Icon name="cancel" size="small" /> Clear selection
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
